import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Image,
  List,
  Popover,
  Space,
  Spin,
  Tabs,
  Timeline,
  Typography,
} from 'antd';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { useFeed } from './shared/context';
import {
  FeedClassPill,
  LogDetail,
  LogSummary,
  MediaImage,
  MediaVideo,
  NeedsReviewPill,
  PersonAvatar,
  Pill,
  StagePill,
  StatusPill,
  VerdictPill,
  WhyFlagged,
} from './shared/components';
import {
  DELETE_REASON_LABEL,
  LOG_ACTION_LABEL,
  REPORT_STATUS_STYLE,
  RESTRICT_SCOPE_LABEL,
  UNPUBLIC_STYLE,
} from './shared/labels';
import {
  FeedAdminCommentRow,
  FeedAdminMedia,
  FeedAdminPostDetail,
  FeedLogRow,
  FeedReportGroupRow,
  FeedRestrictionRow,
} from './shared/types';
import {
  adminMediaUrl,
  feedError,
  fmtBytes,
  fmtDate,
  fmtDateTime,
  fmtTime,
  personText,
  toStringList,
  useFeedList,
} from './shared/utils';
import { ContentActionButtons, ContentActionsMenu, contentActions, useContentActions } from './moderate-modal';
import ResolveModal from './resolve-modal';
import RestrictModal from './restrict-modal';
import styles from './styles.module.scss';

// ------------------------------------------------------------------ media

const mediaSources = (m: FeedAdminMedia) =>
  m.isPublic === false
    ? { url: adminMediaUrl(m.id, 'original'), preview: adminMediaUrl(m.id, 'preview') }
    : { url: m.url, preview: m.previewUrl };

const MediaFooter = ({ m }: { m: FeedAdminMedia }) => {
  const labels = toStringList(m.moderationLabels);
  return (
    <div className={styles.mediaItem}>
      <Space size={4} wrap>
        <VerdictPill verdict={m.moderation} />
        {m.isPublic === false && (
          <Pill color={UNPUBLIC_STYLE.color} bg={UNPUBLIC_STYLE.bg}>
            {/* Video đang xử lý / ảnh đang chờ AI soi cũng isPublic=false nhưng chưa từng công khai. */}
            {m.status !== 'READY' || m.moderation === 'PENDING' ? 'Chưa công khai' : UNPUBLIC_STYLE.label}
          </Pill>
        )}
      </Space>
      {labels.length > 0 && <span>Nhãn AI: {labels.join(', ')}</span>}
      {(m.sourceName || m.bytes) && (
        <span style={{ wordBreak: 'break-all' }}>
          {[m.sourceName, fmtBytes(m.bytes)].filter(Boolean).join(' · ')}
        </span>
      )}
    </div>
  );
};

const MediaSection = ({ media }: { media: FeedAdminMedia[] }) => {
  if (!media?.length) return null;
  const images = media.filter((m) => m.kind === 'IMAGE');
  const videos = media.filter((m) => m.kind === 'VIDEO');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {images.length > 0 && (
        <Image.PreviewGroup>
          <div className={styles.mediaGrid}>
            {images.map((m) => {
              const src = mediaSources(m);
              return (
                <div key={m.id} className={styles.mediaItem}>
                  <MediaImage url={src.url} previewUrl={src.preview} placeholderColor={m.placeholderColor} />
                  <MediaFooter m={m} />
                </div>
              );
            })}
          </div>
        </Image.PreviewGroup>
      )}
      {videos.map((m) => {
        const src = mediaSources(m);
        return (
          <div key={m.id} className={styles.mediaItem}>
            {m.status === 'READY' ? (
              <MediaVideo url={src.url} poster={src.preview} />
            ) : (
              <div className={styles.box} style={{ textAlign: 'center', color: colors.textSecondary }}>
                {m.status === 'FAILED'
                  ? `Video lỗi: ${m.failText || m.failReason || 'không xử lý được'}`
                  : 'Đang xử lý video…'}
              </div>
            )}
            <MediaFooter m={m} />
          </div>
        );
      })}
    </div>
  );
};

// ------------------------------------------------------------------ bình luận (D12)

const CommentsPane = ({ postId }: { postId: number }) => {
  const { refreshKey, myRank } = useFeed();
  const [page, setPage] = useState(1);
  const { items, total, loading } = useFeedList<FeedAdminCommentRow>(
    API.feedAdminComments,
    { postId, page, limit: 20 },
    { refreshKey }
  );
  const { run, modals } = useContentActions();
  return (
    <>
      <List
        loading={loading}
        dataSource={items}
        locale={{ emptyText: 'Chưa có bình luận.' }}
        pagination={
          total > 20
            ? { current: page, pageSize: 20, total, showSizeChanger: false, onChange: setPage, size: 'small' }
            : false
        }
        renderItem={(c) => (
          <List.Item
            key={c.id}
            actions={[
              <ContentActionsMenu
                key="more"
                size="small"
                items={contentActions('comment', c, { myRank, canMarkImportant: false })}
                onRun={(key) => run('comment', c.id, key)}
              />,
            ]}
          >
            <List.Item.Meta
              avatar={<PersonAvatar name={c.author?.name} avatar={c.author?.avatar} size={32} />}
              title={
                <Space size={6} wrap>
                  <span style={{ fontWeight: 600 }}>{c.author?.name || `#${c.author?.userId}`}</span>
                  <FeedClassPill userClass={c.author?.userClass} classLabel={c.author?.classLabel} />
                  <StatusPill status={c.status} deletedAt={c.deletedAt} />
                  {c.needsReview && !c.deletedAt && <NeedsReviewPill />}
                  <span style={{ fontSize: 12, color: colors.textSecondary, fontWeight: 400 }}>
                    {fmtDateTime(c.createdAt)} · #{c.id}
                  </span>
                </Space>
              }
              description={
                <div style={{ color: colors.textPrimary }}>
                  {c.replyTo && (
                    <div style={{ fontSize: 12, color: colors.textSecondary }}>↳ trả lời @{c.replyTo.name}</div>
                  )}
                  <div className={styles.content} style={{ fontSize: 14 }}>
                    {c.content}
                  </div>
                  {c.hiddenNote && (
                    <div style={{ fontSize: 12, color: colors.dangerInk }}>Lý do ẩn: {c.hiddenNote}</div>
                  )}
                  <WhyFlagged keywordHits={c.keywordHits} textVerdict={c.textVerdict} />
                </div>
              }
            />
          </List.Item>
        )}
      />
      {modals}
    </>
  );
};

// ------------------------------------------------------------------ nhật ký

export const LogTimeline = ({ logs }: { logs: FeedLogRow[] }) => {
  if (!logs?.length) return <Empty description="Chưa có dòng nhật ký nào." />;
  const sorted = [...logs].sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : b.id - a.id));
  return (
    <Timeline
      items={sorted.map((row) => ({
        key: row.id,
        children: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Space size={6} wrap>
              <span style={{ fontSize: 12, color: colors.textSecondary }}>
                {fmtDate(row.createdAt)} {fmtTime(row.createdAt, true)}
              </span>
              <StagePill stage={row.stage} />
              <strong>{LOG_ACTION_LABEL[row.action] || row.action}</strong>
              {row.verdict && <VerdictPill verdict={row.verdict} />}
              <span style={{ color: colors.textSecondary }}>
                {row.actorUserId ? personText(row.actor, row.actorUserId) : 'Máy'}
              </span>
            </Space>
            <LogSummary row={row} />
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: colors.textSecondary }}>Chi tiết</summary>
              <div style={{ marginTop: 6 }}>
                <LogDetail row={row} />
              </div>
            </details>
          </div>
        ),
      }))}
    />
  );
};

// ------------------------------------------------------------------ thẻ tác giả

const AuthorCard = ({
  detail,
  onPostsOfUser,
  onRestrict,
}: {
  detail: FeedAdminPostDetail;
  onPostsOfUser: () => void;
  onRestrict: () => void;
}) => {
  const a = detail.author;
  const [active, setActive] = useState<FeedRestrictionRow[] | null>(null);
  const userId = a?.type === 'PAGE' ? detail.actedBy?.userId : a?.userId;
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    AxiosUtils.get(API.feedAdminRestrictions, { userId, active: true, page: 1, limit: 5 })
      .then((res: any) => {
        const list = res?.data?.data ?? res?.data ?? [];
        if (alive) setActive(Array.isArray(list) ? list : []);
      })
      .catch(() => alive && setActive([]));
    return () => {
      alive = false;
    };
  }, [userId]);
  return (
    <div style={{ width: 280, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div>
        <div style={{ fontWeight: 700 }}>{a?.type === 'PAGE' ? detail.actedBy?.name : a?.name}</div>
        {a?.username && <div style={{ fontSize: 12, color: colors.textSecondary }}>@{a.username}</div>}
        {a?.type === 'USER' && <FeedClassPill userClass={a.userClass} classLabel={a.classLabel} />}
        {a?.type === 'PAGE' && (
          <div style={{ fontSize: 12, color: colors.textSecondary }}>Người đăng bài tên HP Media</div>
        )}
      </div>
      {active && active.length > 0 && (
        <div style={{ fontSize: 12, color: colors.dangerInk }}>
          Đang bị hạn chế:{' '}
          {active
            .map((r) => `${RESTRICT_SCOPE_LABEL[r.scope] || r.scope} ${r.endsAt ? `đến ${fmtDateTime(r.endsAt)}` : 'vô thời hạn'}`)
            .join('; ')}
        </div>
      )}
      {userId && (
        <Space wrap>
          {a?.type === 'USER' && (
            <Button size="small" onClick={onPostsOfUser}>
              Bài của người này
            </Button>
          )}
          <Button size="small" danger onClick={onRestrict}>
            Hạn chế người này
          </Button>
        </Space>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ ngăn chi tiết

/** Ngăn chi tiết bài (03 §7) — D4, tải lại khi refreshKey đổi. */
const PostDrawer = ({
  postId,
  initialTab,
  onClose,
}: {
  postId: number | null;
  initialTab?: string | null;
  onClose: () => void;
}) => {
  const { refreshKey, myRank, access, goTab, reportReasonLabel } = useFeed();
  const [detail, setDetail] = useState<FeedAdminPostDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(initialTab || 'content');
  const [resolveOpen, setResolveOpen] = useState(false);
  const [restrictUser, setRestrictUser] = useState<{ userId: number; name: string } | null>(null);
  const { run, modals } = useContentActions();

  useEffect(() => {
    setTab(initialTab || 'content');
  }, [postId, initialTab]);

  useEffect(() => {
    if (!postId) {
      setDetail(null);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    AxiosUtils.get(`${API.feedAdminPosts}/${postId}`)
      .then((res: any) => {
        if (!alive) return;
        setDetail(res?.data as FeedAdminPostDetail);
        setError(null);
      })
      .catch((err) => alive && setError(feedError(err)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [postId, refreshKey]);

  const width = typeof window !== 'undefined' ? Math.min(760, window.innerWidth) : 760;
  const d = detail && detail.id === postId ? detail : null;
  const openReports = (d?.reports || []).filter((r) => r.status === 'OPEN');
  const importantEver = !!d && (d.isImportant || !!d.importantUntil);

  const authorName = d ? (d.author?.type === 'PAGE' ? 'HP Media' : d.author?.name || `#${d.author?.userId}`) : '';
  const restrictTarget = d
    ? d.author?.type === 'PAGE'
      ? d.actedBy
        ? { userId: d.actedBy.userId, name: d.actedBy.name }
        : null
      : d.author?.userId
      ? { userId: d.author.userId, name: d.author.name }
      : null
    : null;

  const resolveGroup: FeedReportGroupRow | null = d
    ? {
        targetType: 'POST',
        targetId: d.id,
        postId: d.id,
        targetUser: restrictTarget ? { userId: restrictTarget.userId, name: restrictTarget.name } : null,
        reportCount: openReports.length,
        reporterCount: new Set(openReports.map((r) => r.reporter?.userId)).size,
        firstAt: openReports[0]?.createdAt || d.createdAt,
        lastAt: openReports[openReports.length - 1]?.createdAt || d.createdAt,
        reasons: Array.from(new Set(openReports.map((r) => r.reason))),
        preview: d.contentPreview,
        targetStatus: d.deletedAt ? 'DELETED' : d.status,
        overdue: false,
      }
    : null;

  const header = d ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Popover
        trigger="click"
        placement="bottomLeft"
        content={
          <AuthorCard
            detail={d}
            onPostsOfUser={() => {
              onClose();
              if (d.author?.userId) {
                goTab('posts', { authorId: String(d.author.userId), authorName: d.author.name });
              }
            }}
            onRestrict={() => restrictTarget && setRestrictUser(restrictTarget)}
          />
        }
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
          <PersonAvatar
            group={d.author?.type === 'PAGE'}
            name={authorName}
            avatar={d.author?.avatar}
            size={40}
          />
          <div>
            <Space size={6} wrap>
              <Typography.Link strong>{authorName}</Typography.Link>
              {d.author?.type === 'USER' ? (
                <FeedClassPill userClass={d.author.userClass} classLabel={d.author.classLabel} />
              ) : (
                d.actedBy?.name && (
                  <span style={{ fontSize: 13, color: colors.textSecondary }}>do {d.actedBy.name} đăng</span>
                )
              )}
            </Space>
            <div style={{ fontSize: 12, color: colors.textSecondary }}>
              Đăng {fmtDateTime(d.createdAt)}
              {d.editedAt ? ` · sửa ${fmtTime(d.editedAt)}` : ''}
            </div>
          </div>
        </div>
      </Popover>
      {d.deletedAt && (
        <div style={{ color: colors.dangerInk, fontSize: 13 }}>
          Đã xoá lúc {fmtDateTime(d.deletedAt)}
          {d.deleteReason ? ` — ${DELETE_REASON_LABEL[d.deleteReason] || d.deleteReason}` : ''}
        </div>
      )}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          background: colors.surface,
          padding: '8px 0',
          borderBottom: `1px solid ${colors.line}`,
        }}
      >
        <ContentActionButtons
          size="small"
          items={contentActions('post', d, { myRank, canMarkImportant: access.canMarkImportant })}
          onRun={(key) => run('post', d.id, key)}
        />
      </div>
    </div>
  ) : null;

  const tabs = d
    ? [
        {
          key: 'content',
          label: 'Nội dung',
          children: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className={styles.content}>
                {d.content || <span style={{ color: colors.textMuted }}>(không có chữ)</span>}
              </div>
              {(d.keywordHits?.length > 0 || (d.textVerdict && d.textVerdict !== 'SKIPPED')) && (
                <div className={styles.box}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Máy kiểm tra</div>
                  <WhyFlagged keywordHits={d.keywordHits} textVerdict={d.textVerdict} />
                </div>
              )}
              <MediaSection media={d.media || []} />
            </div>
          ),
        },
        {
          key: 'comments',
          label: `Bình luận (${d.commentCount ?? 0})`,
          children: <CommentsPane postId={d.id} />,
        },
        {
          key: 'reports',
          label: `Báo cáo (${d.reports?.length ?? 0})`,
          children: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {openReports.length > 0 && (
                <div>
                  <Button type="primary" onClick={() => setResolveOpen(true)}>
                    Xử lý các báo cáo này
                  </Button>
                </div>
              )}
              <List
                dataSource={d.reports || []}
                locale={{ emptyText: 'Bài này chưa bị báo cáo.' }}
                renderItem={(r) => {
                  const s = REPORT_STATUS_STYLE[r.status];
                  return (
                    <List.Item key={r.id}>
                      <List.Item.Meta
                        avatar={<PersonAvatar name={r.reporter?.name} avatar={r.reporter?.avatar} size={32} />}
                        title={
                          <Space size={6} wrap>
                            <span style={{ fontWeight: 600 }}>{personText(r.reporter)}</span>
                            <Pill color={colors.textPrimary} bg={colors.surfaceMuted}>
                              {reportReasonLabel(r.reason)}
                            </Pill>
                            {s && (
                              <Pill color={s.color} bg={s.bg}>
                                {s.label}
                              </Pill>
                            )}
                            <span style={{ fontSize: 12, color: colors.textSecondary, fontWeight: 400 }}>
                              {fmtDateTime(r.createdAt)}
                            </span>
                          </Space>
                        }
                        description={r.note || undefined}
                      />
                    </List.Item>
                  );
                }}
              />
            </div>
          ),
        },
        ...(importantEver
          ? [
              {
                key: 'reads',
                label: 'Đã đọc',
                children: (
                  <div className={styles.box}>
                    <div style={{ fontSize: 15 }}>
                      <strong>{d.seenCount ?? 0}</strong> người đã xem · <strong>{d.ackCount ?? 0}</strong> người bấm
                      "Tôi đã đọc"
                    </div>
                    <div className={styles.hint} style={{ marginTop: 6 }}>
                      Danh sách tên người đã đọc / chưa đọc sẽ có ở giai đoạn sau.
                    </div>
                  </div>
                ),
              },
            ]
          : []),
        {
          key: 'logs',
          label: 'Nhật ký',
          children: <LogTimeline logs={d.logs || []} />,
        },
      ]
    : [];

  return (
    <Drawer
      open={!!postId}
      width={width}
      onClose={onClose}
      destroyOnHidden
      title={
        <Space size={8} wrap>
          <span>Bài #{postId}</span>
          {d && <StatusPill status={d.status} deletedAt={d.deletedAt} />}
          {d?.needsReview && !d.deletedAt && <NeedsReviewPill />}
        </Space>
      }
    >
      {loading && !d ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : error && !d ? (
        <Alert type="error" showIcon message="Không tải được bài" description={error} />
      ) : d ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {header}
          <Tabs size="small" activeKey={tab} onChange={setTab} items={tabs} />
        </div>
      ) : null}
      {modals}
      {resolveOpen && resolveGroup && (
        <ResolveModal group={resolveGroup} onClose={() => setResolveOpen(false)} />
      )}
      <RestrictModal
        open={!!restrictUser}
        presetUser={restrictUser}
        defaultScope="POST"
        onClose={() => setRestrictUser(null)}
      />
    </Drawer>
  );
};

export default PostDrawer;
